import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLang } from '../context/LangContext';
import axios from 'axios';
import { API_URL, resolveImage } from '../config';

const LIVRAISON     = 15;
const SEUIL_GRATUIT = 150;

const CheckoutPage = () => {
  const { items, total, dispatch } = useCart();
  const { t } = useLang();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', governorate: '', zip: '',
    payment: 'cash',
  });
  const [submitted, setSubmitted]   = useState(false);
  const [errors, setErrors]         = useState({});
  const [loading, setLoading]       = useState(false);
  const [submitError, setSubmitError] = useState('');

  const fraisLivraison = total >= SEUIL_GRATUIT ? 0 : LIVRAISON;
  const totalFinal     = total + fraisLivraison;
  const fmt = (n) => n.toLocaleString() + ' TND';

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = t('required');
    if (!form.lastName.trim())  e.lastName  = t('required');
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = t('invalid_email');
    if (!form.phone.match(/^[0-9+\s-]{8,}$/))             e.phone = t('invalid_phone');
    if (!form.address.trim())     e.address     = t('required');
    if (!form.city.trim())        e.city        = t('required');
    if (!form.governorate.trim()) e.governorate = t('required');
    return e;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/orders`, {
        firstName:   form.firstName,
        lastName:    form.lastName,
        email:       form.email,
        phone:       form.phone,
        address:     form.address,
        city:        form.city,
        governorate: form.governorate,
        payment:     form.payment,
        items: items.map(i => ({
          productId: i.productId ?? i.id,
          variantId: i.variantId ?? null,
          name: i.name, price: i.price, quantity: i.quantity, image: i.image,
        })),
        subtotal:    total,
        shipping:    fraisLivraison,
        total:       totalFinal,
      });
      // Succès confirmé par le serveur uniquement : on vide le panier et on affiche la confirmation.
      setSubmitted(true);
      dispatch({ type: 'CLEAR_CART' });
    } catch (err) {
      const status  = err.response?.status;
      const apiMsg  = err.response?.data?.message;
      if (status === 409) {
        // Stock insuffisant sur un ou plusieurs articles
        setSubmitError(apiMsg || t('checkout_stock_error'));
      } else if (err.response) {
        setSubmitError(apiMsg || t('checkout_generic_error'));
      } else {
        // Pas de réponse du serveur (backend injoignable, réseau, etc.)
        setSubmitError(t('checkout_network_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0 && !submitted) return (
    <div className="not-found-page">
      <span>🛒</span>
      <h2>{t('cart_empty')}</h2>
      <Link to="/" className="btn btn--primary">{t('back_home')}</Link>
    </div>
  );

  if (submitted) return (
    <div className="order-success">
      <div className="order-success__card">
        <div className="order-success__icon">✅</div>
        <h2>{t('order_success_title')}</h2>
        <p>{t('order_success_sub')}</p>
        <p className="order-success__total">{t('order_total')} : <strong>{fmt(totalFinal)}</strong></p>
        <Link to="/" className="btn btn--primary">{t('continue_shopping')}</Link>
      </div>
    </div>
  );

  const tunisianGovernorates = ['Tunis','Ariana','Ben Arous','Manouba','Nabeul','Zaghouan','Bizerte','Béja','Jendouba','Kef','Siliana','Sousse','Monastir','Mahdia','Sfax','Kairouan','Kasserine','Sidi Bouzid','Gabès','Médenine','Tataouine','Gafsa','Tozeur','Kébili'];

  const Field = ({ label, name, type = 'text', options }) => (
    <div className="form-field">
      <label>{label}</label>
      {options ? (
        <select name={name} value={form[name]} onChange={handleChange} className={errors[name] ? 'input--error' : ''}>
          <option value="">{t('select_governorate')}</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} name={name} value={form[name]} onChange={handleChange} className={errors[name] ? 'input--error' : ''} />
      )}
      {errors[name] && <span className="field-error">{errors[name]}</span>}
    </div>
  );

  return (
    <div className="checkout-page">
      <div className="breadcrumb">
        <div className="breadcrumb__inner">
          <Link to="/">{t('nav_home')}</Link> <span>›</span>
          <Link to="/" onClick={e => { e.preventDefault(); navigate(-1); }}>{t('cart_title')}</Link> <span>›</span>
          <span>{t('checkout_title')}</span>
        </div>
      </div>

      <div className="checkout-page__inner">
        <form className="checkout-form" onSubmit={handleSubmit} noValidate>
          <h2 className="checkout-section-title">{t('delivery_info')}</h2>

          <div className="form-row">
            <Field label={t('first_name')} name="firstName" />
            <Field label={t('last_name')}  name="lastName"  />
          </div>
          <div className="form-row">
            <Field label={t('email')} name="email" type="email" />
            <Field label={t('phone')} name="phone" type="tel"   />
          </div>
          <Field label={t('address')} name="address" />
          <div className="form-row">
            <Field label={t('city')} name="city" />
            <Field label={t('governorate')} name="governorate" options={tunisianGovernorates} />
          </div>

          <h2 className="checkout-section-title" style={{ marginTop: 32 }}>{t('payment_method')}</h2>
          <div className="payment-methods">
            {[
              { id: 'cash',     icon: '💵', label: t('pay_cash') },
              { id: 'transfer', icon: '🏦', label: t('pay_transfer') },
            ].map(m => (
              <label key={m.id} className={`payment-method ${form.payment === m.id ? 'payment-method--active' : ''}`}>
                <input type="radio" name="payment" value={m.id} checked={form.payment === m.id} onChange={handleChange} />
                <span className="pm-icon">{m.icon}</span>
                <span className="pm-label">{m.label}</span>
              </label>
            ))}
          </div>

          {submitError && (
            <div className="checkout-error" role="alert">
              ⚠️ {submitError}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--full" style={{ marginTop: 24, padding: '16px' }} disabled={loading}>
            {loading ? '⏳ Envoi en cours...' : `✓ ${t('place_order')} — ${fmt(totalFinal)}`}
          </button>
        </form>

        {/* Order summary */}
        <div className="checkout-summary">
          <h2 className="checkout-section-title">{t('order_summary')}</h2>
          <div className="checkout-items">
            {items.map(item => (
              <div className="checkout-item" key={item.id}>
                <img src={resolveImage(item.image)} alt={item.name} />
                <div className="checkout-item__info">
                  <p>{item.name}</p>
                  <small>x{item.quantity}</small>
                </div>
                <strong>{(item.price * item.quantity).toLocaleString()} TND</strong>
              </div>
            ))}
          </div>
          <div className="checkout-totals">
            <div className="checkout-total-row"><span>{t('cart_subtotal')}</span><span>{fmt(total)}</span></div>
            <div className="checkout-total-row">
              <span>{t('cart_shipping')}</span>
              <span className={fraisLivraison === 0 ? 'free-text' : ''}>{fraisLivraison === 0 ? t('cart_shipping_free') : fmt(LIVRAISON)}</span>
            </div>
            <div className="checkout-total-row checkout-total-row--final">
              <strong>{t('cart_total')}</strong>
              <strong>{fmt(totalFinal)}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
